import React from 'react'
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import moment from 'moment-timezone'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { FindTimeView, ITemporarySlot, IBlockInfo } from '../../types'
import { formatMinute } from '../../helpers'

interface IFindTimeCalendarProps {
  show: boolean
  view: FindTimeView
  currentDate: Date
  selectedAttendeeFilter: string[]
  allAttendees: IAssigneeOptions[]
  getAttendeeEvents: {
    [key: string]: Array<{
      date: string
      start: number
      end: number
      title?: string
      startMinute: number
      endMinute: number
    }>
  }
  attendeeColors: { [key: string]: string }
  temporarySlot: ITemporarySlot | null
  timezone: string
  timeGridRef: React.RefObject<HTMLDivElement>
  isCalendarClickRef: React.MutableRefObject<boolean>
  onViewChange: (view: FindTimeView) => void
  onDateChange: (date: Date) => void
  onMonthChange: (monthIndex: number) => void
  onAttendeeFilterChange: (filter: string[]) => void
  onTimeSlotClick: (date: Date, hour: number, startMinute: number) => void
  onBlockClick: (block: IBlockInfo & { date: Date }) => void
}

const FindTimeCalendar: React.FC<IFindTimeCalendarProps> = ({
  show,
  view,
  currentDate,
  selectedAttendeeFilter,
  allAttendees,
  getAttendeeEvents,
  attendeeColors,
  temporarySlot,
  timezone,
  timeGridRef,
  isCalendarClickRef,
  onViewChange,
  onDateChange,
  onMonthChange,
  onAttendeeFilterChange,
  onTimeSlotClick,
  onBlockClick,
}) => {
  if (!show) return null

  // Generate dates based on view
  const dates: Date[] = []
  if (view === 'Day') {
    dates.push(new Date(currentDate))
  } else if (view === '3 Days') {
    for (let i = 0; i < 3; i++) {
      const date = new Date(currentDate)
      date.setDate(date.getDate() + i)
      dates.push(date)
    }
  } else if (view === 'Week') {
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate)
      date.setDate(date.getDate() + i)
      dates.push(date)
    }
  }

  // Generate hours (0-23 for 24-hour format)
  const hours = Array.from({ length: 24 }, (_, i) => i)

  // Get current time in the selected timezone
  const currentDateInTZ = moment.tz(timezone).toDate()
  const currentHourInTZ = currentDateInTZ.getHours()
  const currentMinutesInTZ = currentDateInTZ.getMinutes()

  // Month navigation
  const currentMonth = currentDate.getMonth()
  const currentYear = currentDate.getFullYear()
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  // Get date range display
  const firstDate = dates[0]
  const lastDate = dates[dates.length - 1]
  const dateRangeDisplay =
    dates.length === 1
      ? firstDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      : firstDate.getMonth() === lastDate.getMonth()
      ? `${firstDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })} - ${lastDate.getDate()}`
      : `${firstDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })} - ${lastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`

  return (
    <div className="border-t border-primary-outline pt-4 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        {/* Month Selector */}
        <div className="relative">
          <select
            value={currentMonth}
            onChange={(e) => onMonthChange(parseInt(e.target.value))}
            className="appearance-none bg-white border border-primary-outline rounded-lg px-4 py-2 pr-10 text-sm font-medium cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-brand"
          >
            {monthNames.map((month, index) => (
              <option key={index} value={index}>
                {month} {currentYear}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Navigation and View Controls */}
        <div className="flex items-center gap-4">
          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const newDate = new Date(currentDate)
                if (view === 'Day') {
                  newDate.setDate(newDate.getDate() - 1)
                } else if (view === '3 Days') {
                  newDate.setDate(newDate.getDate() - 3)
                } else if (view === 'Week') {
                  newDate.setDate(newDate.getDate() - 7)
                }
                onDateChange(newDate)
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <span className="text-sm font-medium min-w-[120px] text-center">
              {dateRangeDisplay}
            </span>

            <button
              onClick={() => {
                const newDate = new Date(currentDate)
                if (view === 'Day') {
                  newDate.setDate(newDate.getDate() + 1)
                } else if (view === '3 Days') {
                  newDate.setDate(newDate.getDate() + 3)
                } else if (view === 'Week') {
                  newDate.setDate(newDate.getDate() + 7)
                }
                onDateChange(newDate)
              }}
              className="p-1 rounded hover:bg-primary-brandLight text-primary-text transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* View Mode Selector */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onViewChange('Day')}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                view === 'Day'
                  ? 'bg-white text-primary-brand font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Day
            </button>
            <button
              onClick={() => onViewChange('3 Days')}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                view === '3 Days'
                  ? 'bg-white text-primary-brand font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              3 Days
            </button>
            <button
              onClick={() => onViewChange('Week')}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                view === 'Week'
                  ? 'bg-white text-primary-brand font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Week
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border border-primary-outline rounded-lg">
        <div className="overflow-x-auto">
          <div className="inline-block" style={{ minWidth: view === 'Week' ? '900px' : '100%' }}>
            {/* Header Row */}
            <div
              className="flex bg-gray-50 pr-[var(--scrollbar-width,0px)]"
              style={{ '--scrollbar-width': '15px' } as React.CSSProperties}
            >
              <div className="w-20 flex-shrink-0 border-r border-primary-outline" />
              {dates.map((date, index) => (
                <div
                  key={index}
                  className={`py-3 text-center border-r last:border-r-0 border-primary-outline ${
                    date.toDateString() === currentDateInTZ.toDateString()
                      ? 'bg-primary-brandLight bg-opacity-10'
                      : ''
                  }`}
                  style={{
                    flex: '1 1 0',
                    minWidth: view === 'Week' ? '120px' : view === '3 Days' ? '160px' : 'auto',
                  }}
                >
                  <div className="text-xs text-gray-600 font-medium">
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div
                    className={`text-lg font-semibold mt-1 ${
                      date.toDateString() === currentDateInTZ.toDateString()
                        ? 'text-primary-brand'
                        : ''
                    }`}
                  >
                    {date.getDate()}
                  </div>
                </div>
              ))}
            </div>

            {/* Time Grid */}
            <div ref={timeGridRef} className="max-h-[400px] overflow-y-auto">
              {hours.map((hour, hourIndex) => {
                const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
                const period = hour < 12 ? 'AM' : 'PM'
                const isFirstHour = hourIndex === 0

                return (
                  <div key={hour} className="flex">
                    {/* Time Label */}
                    <div className="w-20 flex-shrink-0 border-r border-primary-outline relative text-right pr-3">
                      <span
                        className={`text-xs text-gray-600 absolute right-3 ${
                          isFirstHour ? 'top-0' : '-top-2'
                        }`}
                      >
                        {hour12} {period}
                      </span>
                    </div>

                    {/* Date Columns */}
                    {dates.map((date, dateIndex) => {
                      const isToday = date.toDateString() === currentDateInTZ.toDateString()
                      const isCurrentHour = isToday && hour === currentHourInTZ
                      const isLastHour = hourIndex === hours.length - 1

                      // Calculate the exact position of the current time line within the hour
                      const minutePercentage = currentMinutesInTZ / 60 // 0 to 1

                      // Find busy times for this hour and date
                      const busyBlocks: Array<{
                        attendee: IAssigneeOptions
                        title?: string
                        color: string
                        startMinute: number
                        endMinute: number
                        startHour: number
                        endHour: number
                      }> = []

                      if (selectedAttendeeFilter.includes('all')) {
                        // Show all attendees' busy times separately (no limit)
                        allAttendees.forEach((attendee) => {
                          const events = getAttendeeEvents[attendee.value] || []

                          events.forEach((event) => {
                            const dateMatch = event.date === date.toDateString()
                            // Check if this hour cell should display this event
                            // Event should be displayed if it starts in this hour
                            const shouldDisplay = dateMatch && event.start === hour

                            if (shouldDisplay) {
                              busyBlocks.push({
                                attendee,
                                title: event.title,
                                color: attendeeColors[attendee.value],
                                startMinute: event.startMinute,
                                endMinute: event.endMinute,
                                startHour: event.start,
                                endHour: event.end,
                              })
                            }
                          })
                        })
                      } else {
                        // Show selected attendees' busy times separately (no overlap limit)
                        selectedAttendeeFilter.forEach((userId) => {
                          const events = getAttendeeEvents[userId] || []
                          const attendee = allAttendees.find((a) => a.value === userId)
                          if (attendee) {
                            events.forEach((event) => {
                              const dateMatch = event.date === date.toDateString()
                              const shouldDisplay = dateMatch && event.start === hour

                              if (shouldDisplay) {
                                busyBlocks.push({
                                  attendee,
                                  title: event.title,
                                  color: attendeeColors[attendee.value],
                                  startMinute: event.startMinute,
                                  endMinute: event.endMinute,
                                  startHour: event.start,
                                  endHour: event.end,
                                })
                              }
                            })
                          }
                        })
                      }

                      // Show all blocks without limiting
                      const displayedBlocks = busyBlocks

                      return (
                        <div
                          key={dateIndex}
                          className={`h-24 border-r last:border-r-0 border-t cursor-pointer relative overflow-visible ${
                            isLastHour ? 'border-b' : ''
                          } border-primary-outline ${
                            isCurrentHour
                              ? 'bg-primary-brandLight bg-opacity-20'
                              : 'hover:bg-gray-50'
                          }`}
                          style={{
                            flex: '1 1 0',
                            minWidth:
                              view === 'Week' ? '120px' : view === '3 Days' ? '160px' : 'auto',
                          }}
                          onClick={(e) => {
                            // Calculate which quarter (15-minute interval) was clicked
                            const rect = e.currentTarget.getBoundingClientRect()
                            const clickY = e.clientY - rect.top
                            const cellHeight = rect.height
                            const quarterClicked = Math.floor((clickY / cellHeight) * 4) // 0, 1, 2, or 3

                            // Calculate start minute based on quarter
                            const startMinute = quarterClicked * 15 // 0, 15, 30, or 45

                            // Set flag to indicate this is a calendar click (prevents clearing temporary slot)
                            isCalendarClickRef.current = true

                            // Trigger time slot click
                            onTimeSlotClick(new Date(date), hour, startMinute)
                          }}
                        >
                          {/* Busy time blocks - with minute-level precision */}
                          {displayedBlocks.map((block, blockIndex) => {
                            // Simple layout: blocks side by side, equal width
                            const numBlocks = displayedBlocks.length
                            const blockWidth = `${100 / numBlocks}%`
                            const leftPosition = `${(100 / numBlocks) * blockIndex}%`

                            // Calculate vertical position and height for multi-hour events
                            // The hour cell is 64px (h-16)
                            const topPercentage = (block.startMinute / 60) * 100 // Position within first hour

                            // Calculate total duration in minutes
                            const hoursDifference = block.endHour - block.startHour
                            const totalDurationMinutes =
                              hoursDifference * 60 + (block.endMinute - block.startMinute)

                            // Height as percentage of a single hour cell, can be > 100% for multi-hour events
                            const heightPercentage = (totalDurationMinutes / 60) * 100

                            // Format time display using actual start hour and minute
                            const startHour12 =
                              block.startHour === 0
                                ? 12
                                : block.startHour > 12
                                ? block.startHour - 12
                                : block.startHour
                            const endHour12 =
                              block.endHour === 0
                                ? 12
                                : block.endHour > 12
                                ? block.endHour - 12
                                : block.endHour
                            const startPeriod = block.startHour < 12 ? 'AM' : 'PM'
                            const endPeriod = block.endHour < 12 ? 'AM' : 'PM'
                            const timeDisplay = `${startHour12}:${formatMinute(
                              block.startMinute
                            )} ${startPeriod} - ${endHour12}:${formatMinute(
                              block.endMinute
                            )} ${endPeriod}`

                            // Determine if this is a very short event (30 minutes or less)
                            const isShortEvent = totalDurationMinutes <= 30

                            return (
                              <div
                                key={blockIndex}
                                className="absolute group rounded cursor-pointer hover:shadow-md transition-shadow"
                                style={{
                                  backgroundColor: `${block.color}40`,
                                  borderLeft: `3px solid ${block.color}`,
                                  width: blockWidth,
                                  left: leftPosition,
                                  top: `${topPercentage}%`,
                                  height: `${heightPercentage}%`,
                                  right: blockIndex === numBlocks - 1 ? '1px' : 'auto',
                                  zIndex: 20,
                                  overflow: 'hidden',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onBlockClick({
                                    ...block,
                                    date,
                                  })
                                }}
                              >
                                <div
                                  className={`px-2 h-full flex ${
                                    isShortEvent
                                      ? 'flex-row items-center gap-1.5'
                                      : 'flex-col py-1.5 justify-center'
                                  }`}
                                >
                                  <div
                                    className={`font-semibold leading-tight truncate ${
                                      isShortEvent ? 'text-[10px]' : 'text-xs'
                                    }`}
                                    style={{ color: block.color }}
                                  >
                                    {block.title || 'Busy'}
                                  </div>
                                  {!isShortEvent && (
                                    <div className="text-[10px] text-gray-600 mt-0.5 truncate">
                                      {timeDisplay}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })}

                          {/* Temporary slot preview (15 minutes) */}
                          {temporarySlot &&
                            temporarySlot.date.toDateString() === date.toDateString() &&
                            (() => {
                              // Calculate if this slot spans into this hour
                              const slotStartHour = temporarySlot.startHour
                              const slotStartMinute = temporarySlot.startMinute
                              const totalSlotMinutes = slotStartHour * 60 + slotStartMinute
                              const slotEndMinutes = totalSlotMinutes + 15
                              const slotEndHour = Math.floor(slotEndMinutes / 60)

                              const currentHourStart = hour * 60
                              const currentHourEnd = (hour + 1) * 60

                              // Check if slot overlaps with this hour
                              const overlapsThisHour =
                                (totalSlotMinutes >= currentHourStart &&
                                  totalSlotMinutes < currentHourEnd) ||
                                (slotEndMinutes > currentHourStart &&
                                  slotEndMinutes <= currentHourEnd) ||
                                (totalSlotMinutes <= currentHourStart &&
                                  slotEndMinutes >= currentHourEnd)

                              if (!overlapsThisHour) return null

                              // Calculate the portion of the slot that appears in this hour
                              const visibleStartMinute = Math.max(
                                0,
                                totalSlotMinutes - currentHourStart
                              )
                              const visibleEndMinute = Math.min(
                                60,
                                slotEndMinutes - currentHourStart
                              )
                              const visibleDuration = visibleEndMinute - visibleStartMinute

                              const topPercentage = (visibleStartMinute / 60) * 100
                              const heightPercentage = (visibleDuration / 60) * 100

                              // Check if this is the first hour cell (where the slot actually starts)
                              const isFirstCell = hour === slotStartHour

                              // Only show rounded corners and border on the edges of the full slot
                              const borderRadius = isFirstCell
                                ? slotEndHour === hour
                                  ? '4px'
                                  : '4px 4px 0 0' // Full slot in one hour OR top corners only
                                : '0 0 4px 4px' // Bottom corners only for continuation

                              // Format time display (only show in first cell)
                              const startHour12 =
                                slotStartHour === 0
                                  ? 12
                                  : slotStartHour > 12
                                  ? slotStartHour - 12
                                  : slotStartHour

                              const endMinute = slotEndMinutes % 60
                              const endHour12 =
                                slotEndHour === 0
                                  ? 12
                                  : slotEndHour > 12
                                  ? slotEndHour - 12
                                  : slotEndHour
                              const endPeriod = slotEndHour < 12 ? 'AM' : 'PM'

                              const timeDisplay = `${startHour12}:${formatMinute(
                                slotStartMinute
                              )} - ${endHour12}:${formatMinute(endMinute)} ${endPeriod}`

                              return (
                                <div
                                  className="absolute pointer-events-none border-2 border-primary overflow-hidden"
                                  style={{
                                    backgroundColor: 'rgba(139, 92, 246, 0.15)', // Faded purple
                                    left: '2px',
                                    right: '2px',
                                    top: isFirstCell ? `${topPercentage}%` : '-1px', // Extend up to cover border in continuation
                                    height: isFirstCell
                                      ? `${heightPercentage}%`
                                      : `calc(${heightPercentage}% + 1px)`, // Add 1px to cover border
                                    zIndex: 10, // Higher z-index to overlay the hour border
                                    borderRadius: borderRadius,
                                    // Remove top border if this is a continuation from previous hour
                                    borderTop: isFirstCell ? undefined : 'none',
                                  }}
                                >
                                  {isFirstCell && (
                                    <div className="px-2 py-1">
                                      <div className="text-[10px] font-semibold text-primary truncate">
                                        New Meeting ({timeDisplay})
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            })()}

                          {/* Current time indicator line */}
                          {isCurrentHour && (
                            <div
                              className="absolute left-0 right-0 border-t-2 border-primary-error z-20"
                              style={{
                                top: `${minutePercentage * 100}%`,
                              }}
                            >
                              <div className="absolute -left-1 -top-1 w-2 h-2 bg-primary-error rounded-full" />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Attendee Filter */}
      {allAttendees.length > 0 && (
        <div className="mt-4">
          <div className="text-sm font-medium text-gray-700 mb-2">Filter by attendee:</div>
          <div className="flex flex-wrap gap-2">
            {/* All button */}
            <button
              onClick={() => onAttendeeFilterChange(['all'])}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                selectedAttendeeFilter.includes('all')
                  ? 'bg-primary-brand text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>

            {/* Individual attendee buttons */}
            {allAttendees.map((attendee) => {
              const isSelected = selectedAttendeeFilter.includes(attendee.value)
              const color = attendeeColors[attendee.value]

              return (
                <button
                  key={attendee.value}
                  onClick={() => {
                    if (selectedAttendeeFilter.includes('all')) {
                      // If "All" is selected, switch to only this attendee
                      onAttendeeFilterChange([attendee.value])
                    } else if (isSelected) {
                      // If already selected, remove it
                      const newFilter = selectedAttendeeFilter.filter((id) => id !== attendee.value)
                      // If no attendees left, select "All"
                      onAttendeeFilterChange(newFilter.length === 0 ? ['all'] : newFilter)
                    } else {
                      // Add to selection
                      onAttendeeFilterChange([...selectedAttendeeFilter, attendee.value])
                    }
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    isSelected ? 'shadow-sm' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                  style={{
                    backgroundColor: isSelected ? `${color}20` : undefined,
                    borderLeft: isSelected ? `3px solid ${color}` : undefined,
                    color: isSelected ? color : undefined,
                  }}
                >
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                  {attendee.label}
                  {isSelected && !selectedAttendeeFilter.includes('all') && (
                    <X className="w-3 h-3" />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default FindTimeCalendar
